import React, { Fragment, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
} from "reactstrap";
import { Breadcrumbs, H5 } from "../../AbstractElements";
import { getAddOnById, deleteAddOn } from "../../api/addon.api";
import { toast } from "react-toastify";
import { titleCase } from "../../utils/case";
import { hasRole } from "../../utils/auth";
const InventoryView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canManageInventory = hasRole(
    "superadmin",
    "finance"
  );
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [inventory, setInventory] = useState(null);

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const data = await getAddOnById(id);
        setInventory(data);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, [id]);

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (!inventory) {
    return <div className="text-center mt-5">Inventory not found</div>;
  }

  const parsePricing = (pricing) => {
    if (!pricing) return [];

    if (typeof pricing === "string") {
      try {
        return JSON.parse(pricing);
      } catch {
        return [];
      }
    }

    return Array.isArray(pricing) ? pricing : [];
  };

  const pricingRows = parsePricing(inventory.pricing);
  const lotAddOns = inventory.lotAddOns || [];

  const getBadgeColor = () => {
    if (inventory.inventoryType === "asset") return "primary";
    if (inventory.inventoryType === "consumable") return "warning";
    return "secondary";
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("inventory"))}
        title={titleCase(t("view inventory"))}
        mainTitle={
          <div className="d-flex align-items-center gap-2">
            <span>{inventory.name}</span>
            <Badge color={getBadgeColor()}>
              {titleCase(
                t(inventory.inventoryType?.replace(/_/g, " ") || "-")
              )}
            </Badge>
            <Badge color={inventory.status === "active" ? "success" : "dark"}>
              {titleCase(t(inventory.status || "-"))}
            </Badge>
          </div>
        }
      />

      <Container fluid>
        <Row className="mb-4">
          <Col className="d-flex justify-content-end gap-2 flex-wrap">
            {canManageInventory && (
              <>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => navigate(`/inventory/edit/${id}`)}
                >
                  {titleCase(t("edit"))}
                </button>

                <button
                  className="btn btn-sm btn-danger"
                  disabled={deleting}
                  onClick={async () => {
                    const confirmed = window.confirm(
                      t("are you sure you want to delete this inventory?")
                    );

                    if (!confirmed) return;

                    try {
                      setDeleting(true);
                      await deleteAddOn(id);
                      toast.success(t("deleted successfully"));
                      navigate("/inventory");
                    } catch (err) {
                      toast.error(err.message);
                    } finally {
                      setDeleting(false);
                    }
                  }}
                >
                  {deleting ? t("deleting...") : titleCase(t("delete"))}
                </button>
              </>
            )}

            <button
              className="btn btn-sm btn-dark"
              onClick={() => navigate(-1)}
            >
              {titleCase(t("back"))}
            </button>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md="4">
            <Card className="mb-4 shadow-sm h-100">
              <CardBody>
                <div
                  className="bg-light rounded mb-3 overflow-hidden"
                  style={{ height: "220px" }}
                >
                  {inventory.photo ? (
                    <img
                      src={`${process.env.REACT_APP_FILES_URL}/${inventory.photo}`}
                      alt={inventory.name}
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <i className="fa fa-cube fa-4x text-muted"></i>
                    </div>
                  )}
                </div>

                <p className="text-muted mb-0">
                  {inventory.description || "-"}
                </p>
              </CardBody>
            </Card>
          </Col>

          <Col md="4">
            <Card className="mb-4 shadow-sm h-100">
              <CardBody>
                <div className="p-3 border rounded mb-3">
                  <Row>
                    <Col md="6">
                      <p className="text-muted mb-0">
                        {titleCase(t("category"))}
                      </p>
                    </Col>
                    <Col md="6">
                      <h6 className="mb-0">{inventory.category || "-"}</h6>
                    </Col>
                  </Row>
                </div>

                <div className="p-3 border rounded mb-3">
                  <Row>
                    <Col md="6">
                      <p className="text-muted mb-0">
                        {titleCase(t("inventory type"))}
                      </p>
                    </Col>
                    <Col md="6">
                      <Badge color={getBadgeColor()}>
                        {titleCase(
                          t(inventory.inventoryType?.replace(/_/g, " ") || "-")
                        )}
                      </Badge>
                    </Col>
                  </Row>
                </div>

                <div className="p-3 border rounded mb-3">
                  <Row>
                    <Col md="6">
                      <p className="text-muted mb-0">
                        {titleCase(t("total stock"))}
                      </p>
                    </Col>
                    <Col md="6">
                      <h6 className="mb-0">
                        {inventory.inventoryType === "non_stock"
                          ? "-"
                          : inventory.totalStock ?? "-"}
                      </h6>
                    </Col>
                  </Row>
                </div>

                <div className="p-3 border rounded">
                  <Row>
                    <Col md="6">
                      <p className="text-muted mb-0">
                        {titleCase(t("low stock threshold"))}
                      </p>
                    </Col>
                    <Col md="6">
                      <h6 className="mb-0">
                        {inventory.inventoryType === "non_stock"
                          ? "-"
                          : inventory.lowStockThreshold ?? "-"}
                      </h6>
                    </Col>
                  </Row>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md="4">
            <Card className="mb-4 shadow-sm h-100">
              <CardHeader>
                <H5>{titleCase(t("pricing"))}</H5>
              </CardHeader>

              <CardBody>
                {pricingRows.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-bordered mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{titleCase(t("charged by"))}</th>
                          <th>{titleCase(t("amount"))}</th>
                        </tr>
                      </thead>

                      <tbody>
                        {pricingRows.map((pricing) => (
                          <tr key={pricing.chargedBy}>
                            <td>
                              {titleCase(
                                t(pricing.chargedBy?.replace(/_/g, " ") || "-")
                              )}
                            </td>
                            <td>
                              {pricing.amount != null
                                ? pricing.amount.toLocaleString("en-MY", {
                                    style: "currency",
                                    currency: "MYR",
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })
                                : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="mb-0 text-muted">-</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Card className="mb-4 shadow-sm">
          <CardHeader>
            <H5>{titleCase(t("assigned lots"))}</H5>
          </CardHeader>

          <CardBody>
            {lotAddOns.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{titleCase(t("lot name"))}</th>
                      <th>{titleCase(t("location"))}</th>
                      <th>{titleCase(t("type"))}</th>
                      <th>{titleCase(t("minimum quantity"))}</th>
                      <th>{titleCase(t("maximum quantity"))}</th>
                      <th>{titleCase(t("status"))}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {lotAddOns.map((item) => (
                      <tr key={item.id}>
                        <td>{item.lot?.name || "-"}</td>
                        <td>{item.lot?.location || "-"}</td>
                        <td>
                          {titleCase(t(item.lot?.type || "permanent"))}
                        </td>
                        <td>{item.minQty ?? "-"}</td>
                        <td>{item.maxQty ?? "-"}</td>
                        <td>
                          <Badge
                            color={item.status === "active" ? "success" : "dark"}
                          >
                            {titleCase(t(item.status || "-"))}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mb-0 text-muted">-</p>
            )}
          </CardBody>
        </Card>

        <Card className="mb-4 shadow-sm">
          <CardHeader>
            <H5>{titleCase(t("remarks"))}</H5>
          </CardHeader>

          <CardBody>
            {inventory.remark ? (
              <div dangerouslySetInnerHTML={{ __html: inventory.remark }} />
            ) : (
              <p className="mb-0 text-muted">-</p>
            )}
          </CardBody>
        </Card>
      </Container>
    </Fragment>
  );
};

export default InventoryView;